/* ============================================================
   PawHaven Shopify Theme — main.js
   Shopify-adapted: uses /cart/add.js AJAX API for real cart,
   native Shopify form submissions for newsletter & contact.
   ============================================================ */

'use strict';

/* ---- Helpers ---- */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

/* ---- Toast ---- */
let toastTimer;
function showToast(msg, duration = 3000) {
  const t = $('#toast');
  if (!t) return;
  t.textContent = msg;
  t.style.opacity = '1';
  t.style.transform = 'translateX(-50%) translateY(0)';
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    t.style.opacity = '0';
    t.style.transform = 'translateX(-50%) translateY(80px)';
  }, duration);
}
window.showToast = showToast;

/* ---- Cart: Shopify AJAX API ---- */
async function fetchCartCount() {
  try {
    const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    const cart = await res.json();
    return cart.item_count || 0;
  } catch (_) {
    return 0;
  }
}

function updateCartUI(count) {
  $$('#cart-count').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
  $$('#cart-btn').forEach(btn => {
    btn.setAttribute('aria-label', `Shopping cart, ${count} item${count !== 1 ? 's' : ''}`);
  });
}

async function addToCartShopify(variantId, productName, btn) {
  const original = btn ? btn.textContent : '';
  if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

  const ok = await window.CartDrawer.addToCart(variantId, 1);

  if (ok) {
    showToast(`🛒 "${productName}" added to cart!`);
    if (btn) {
      btn.textContent = '✓ Added!';
      btn.style.background = 'var(--teal)';
      setTimeout(() => {
        btn.textContent = original;
        btn.style.background = '';
        btn.disabled = false;
      }, 1800);
    }
  } else {
    if (btn) { btn.textContent = original; btn.disabled = false; }
    showToast('⚠️ Could not add to cart. Please try again.');
  }
}
window.addToCartShopify = addToCartShopify;

/* ---- Cart Drawer ----
   Slide-out drawer rendered globally (sections/cart-drawer.liquid,
   included once in layout/theme.liquid). Uses the Cart AJAX API's
   `sections` parameter so add/change requests come back with
   freshly-rendered drawer HTML — no separate re-fetch or full
   page reload needed. Event handling uses delegation on
   `document` throughout (rather than binding to the drawer
   element directly) so swapping in new drawer HTML after every
   cart change never leaves stale/duplicate listeners behind. */
const CartDrawer = (function () {
  function getDrawer() {
    return document.getElementById('cart-drawer');
  }

  function open() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function renderDrawerHTML(html) {
    const current = getDrawer();
    if (!current || !current.parentNode) return;
    const wasOpen = current.classList.contains('open');
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const next = temp.querySelector('#cart-drawer');
    if (!next) return;
    if (wasOpen) {
      next.classList.add('open');
      next.setAttribute('aria-hidden', 'false');
    }
    current.parentNode.replaceChild(next, current);
  }

  async function changeQuantity(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: ['cart-drawer'] })
      });
      const data = await res.json();
      if (data.sections && data.sections['cart-drawer']) {
        renderDrawerHTML(data.sections['cart-drawer']);
      }
      if (typeof data.item_count === 'number') updateCartUI(data.item_count);
    } catch (_) {
      showToast('⚠️ Could not update cart. Please try again.');
    }
  }

  async function addToCart(variantId, quantity) {
    quantity = quantity || 1;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity, sections: ['cart-drawer'] })
      });
      if (!res.ok) throw new Error('Cart error');
      const data = await res.json();
      if (data.sections && data.sections['cart-drawer']) {
        renderDrawerHTML(data.sections['cart-drawer']);
      }
      const count = await fetchCartCount();
      updateCartUI(count);
      open();
      return true;
    } catch (err) {
      return false;
    }
  }

  function init() {
    document.addEventListener('click', (e) => {
      const cartBtn = e.target.closest('#cart-btn');
      if (cartBtn) {
        e.preventDefault();
        open();
        return;
      }

      const closeTrigger = e.target.closest('[data-cart-drawer-close]');
      if (closeTrigger) {
        e.preventDefault();
        close();
        return;
      }

      const incBtn = e.target.closest('[data-cart-qty-increase]');
      const decBtn = e.target.closest('[data-cart-qty-decrease]');
      const removeBtn = e.target.closest('[data-cart-item-remove]');
      if (incBtn || decBtn || removeBtn) {
        e.preventDefault();
        const key = (incBtn || decBtn || removeBtn).dataset.key;
        const li = document.querySelector(`[data-cart-item-key="${key}"]`);
        const qtyEl = li ? li.querySelector('.cart-drawer__item-qty span') : null;
        const currentQty = qtyEl ? (parseInt(qtyEl.textContent, 10) || 1) : 1;
        let newQty = 0;
        if (incBtn) newQty = currentQty + 1;
        else if (decBtn) newQty = Math.max(0, currentQty - 1);
        changeQuantity(key, newQty);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
  }

  return { open, close, addToCart, changeQuantity, init };
})();
window.CartDrawer = CartDrawer;

/* ---- Wishlist (local state — no Shopify wishlist app required) ---- */
function toggleWishlist(btn, productName) {
  const svg = btn.querySelector('svg');
  const active = btn.dataset.wishlisted === 'true';
  if (active) {
    btn.dataset.wishlisted = 'false';
    btn.style.color = '';
    if (svg) svg.setAttribute('fill', 'none');
    showToast(`💔 Removed from wishlist`);
  } else {
    btn.dataset.wishlisted = 'true';
    btn.style.color = '#E53E3E';
    if (svg) svg.setAttribute('fill', '#E53E3E');
    showToast(`❤️ Saved to wishlist!`);
  }
}

function initWishlistButtons() {
  $$('[data-wishlist-btn]').forEach(btn => {
    btn.addEventListener('click', () => {
      const name = btn.dataset.product || 'item';
      toggleWishlist(btn, name);
    });
  });
}

/* ---- Sticky Navbar ---- */
function initStickyNav() {
  const nav = $('#navbar');
  if (!nav) return;
  window.addEventListener('scroll', () => {
    nav.classList.toggle('scrolled', window.scrollY > 20);
  }, { passive: true });
}

/* ---- Mobile Menu ---- */
function initMobileMenu() {
  const hamburger = $('#hamburger');
  const menu = $('#mobile-menu');
  if (!hamburger || !menu) return;

  hamburger.addEventListener('click', () => {
    const isOpen = menu.classList.toggle('open');
    hamburger.classList.toggle('open', isOpen);
    hamburger.setAttribute('aria-expanded', String(isOpen));
    document.body.style.overflow = isOpen ? 'hidden' : '';
  });

  $$('a', menu).forEach(link => {
    link.addEventListener('click', () => {
      menu.classList.remove('open');
      hamburger.classList.remove('open');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    });
  });

  document.addEventListener('click', e => {
    if (!hamburger.contains(e.target) && !menu.contains(e.target)) {
      menu.classList.remove('open');
      hamburger.classList.remove('open');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
  });
}

/* ---- Scroll Animations ---- */
function initScrollAnimations() {
  if (!('IntersectionObserver' in window)) return;
  const style = document.createElement('style');
  style.textContent = `.fade-in{opacity:0;transform:translateY(24px);transition:opacity .55s ease,transform .55s ease}.fade-in.visible{opacity:1;transform:translateY(0)}`;
  document.head.appendChild(style);
  const targets = $$('.product-card,.feature-card,.testimonial-card,.category-card,.value-card');
  targets.forEach(el => el.classList.add('fade-in'));
  const observer = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target); } });
  }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
  targets.forEach(el => observer.observe(el));
}

/* ---- Smooth Scroll ---- */
function initSmoothScroll() {
  $$('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const target = document.getElementById(a.getAttribute('href').slice(1));
      if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    });
  });
}

/* ---- Search Shortcut (press /) ---- */
function initSearchShortcut() {
  document.addEventListener('keydown', e => {
    if (e.key === '/' && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
      e.preventDefault();
      const input = $('.navbar__search input');
      if (input) input.focus();
    }
  });
}

/* ---- Product Page: Variant Switcher ----
   Handled entirely in templates/product.liquid, which embeds the
   product's real variant data and updates price/variant-id/
   availability on selection — this file used to have a stub here
   (inline style toggling, mismatched label ids) that never
   actually synced price or variant id; removed to avoid a second,
   conflicting click handler on the same buttons. */

/* ---- Cart Page: live shipping progress ---- */
function initCartProgress() {
  // Already rendered server-side; nothing needed unless making AJAX cart page
}

/* ---- Init ---- */
document.addEventListener('DOMContentLoaded', async () => {
  initStickyNav();
  initMobileMenu();
  initWishlistButtons();
  initSmoothScroll();
  initScrollAnimations();
  initSearchShortcut();
  CartDrawer.init();

  // Sync cart count on page load
  const count = await fetchCartCount();
  updateCartUI(count);
});
